import { useEffect, useRef } from 'react';

interface VisibilityAwareRefreshOptions {
  intervalMs: number;
  onRefresh: () => void;
  staleAfterMs?: number;
}

/**
 * Runs periodic refreshes while the page is visible and refreshes once when a
 * hidden tab becomes visible again. Restarting the interval after a resume
 * refresh prevents a second refresh from firing immediately afterward.
 */
export const useVisibilityAwareRefresh = ({
  intervalMs,
  onRefresh,
  staleAfterMs = 0
}: VisibilityAwareRefreshOptions): void => {
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (intervalMs <= 0) {
      return;
    }

    let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : null;
    let intervalId: number | null = null;

    const clearRefreshInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startRefreshInterval = () => {
      clearRefreshInterval();
      intervalId = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          onRefreshRef.current();
        }
      }, intervalMs);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt ??= Date.now();
        clearRefreshInterval();
        return;
      }

      if (hiddenAt === null) {
        return;
      }

      const hiddenDurationMs = Date.now() - hiddenAt;
      hiddenAt = null;
      if (hiddenDurationMs >= staleAfterMs) {
        onRefreshRef.current();
      }
      startRefreshInterval();
    };

    if (document.visibilityState === 'visible') {
      startRefreshInterval();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearRefreshInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [intervalMs, staleAfterMs]);
};
